import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import { CheckCircleOutlined, WhatsApp } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import SectionContainer from '../components/SectionContainer';
import RevealOnScroll from '../components/RevealOnScroll';
import { submitLibraryLead } from '../leadService';
import useAnalytics from '../../analytics/useAnalytics';
import { EVT_LIBRARY_LEAD_SUBMIT } from '../../analytics/events';
import { INDIAN_CITIES } from '../../utils/indianCities';

const CURRENT_SYSTEM_OPTIONS = [
  { value: 'manual', label: 'Manual register' },
  { value: 'excel', label: 'Excel / Sheets' },
  { value: 'other_platform', label: 'Another platform' },
  { value: 'none', label: 'None' },
];

const TRUST_BULLETS = [
  'Free to list',
  '24-hour onboarding',
  'No long-term contracts',
];

const PHONE_RE = /^[6-9]\d{9}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EMPTY_FORM = {
  ownerName: '',
  phone: '',
  email: '',
  libraryName: '',
  city: null,
  seats: '',
  currentSystem: '',
  message: '',
  wantsWebsite: false,
};

const EMPTY_ERRORS = {
  ownerName: '',
  phone: '',
  email: '',
  libraryName: '',
  city: '',
  seats: '',
  currentSystem: '',
};

function validate(fields) {
  const errs = { ...EMPTY_ERRORS };
  if (!fields.ownerName || fields.ownerName.trim().length < 2) {
    errs.ownerName = 'Name must be at least 2 characters.';
  } else if (fields.ownerName.trim().length > 100) {
    errs.ownerName = 'Name must be under 100 characters.';
  }
  if (!fields.phone || !PHONE_RE.test(fields.phone)) {
    errs.phone = 'Enter a valid 10-digit Indian mobile number.';
  }
  if (!fields.email || !EMAIL_RE.test(fields.email)) {
    errs.email = 'Enter a valid email address.';
  }
  if (!fields.libraryName || fields.libraryName.trim().length < 2) {
    errs.libraryName = 'Library name must be at least 2 characters.';
  } else if (fields.libraryName.trim().length > 200) {
    errs.libraryName = 'Library name must be under 200 characters.';
  }
  if (!fields.city) {
    errs.city = 'Please select a city.';
  }
  const seats = Number(fields.seats);
  if (!fields.seats || isNaN(seats) || seats < 1 || seats > 500) {
    errs.seats = 'Enter a seat count between 1 and 500.';
  }
  if (!fields.currentSystem) {
    errs.currentSystem = 'Please select your current system.';
  }
  return errs;
}

function hasErrors(errs) {
  return Object.values(errs).some(Boolean);
}

function SignupSection() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { track } = useAnalytics();
  const formRef = useRef(null);

  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState(EMPTY_ERRORS);
  const [submitting, setSubmitting] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);

  useEffect(() => {
    function handleWebsiteAddon(e) {
      if (e.detail === true) {
        setForm((prev) => ({ ...prev, wantsWebsite: true }));
        if (formRef.current) {
          formRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }
    window.addEventListener('bml:toggle-website-addon', handleWebsiteAddon);
    return () => window.removeEventListener('bml:toggle-website-addon', handleWebsiteAddon);
  }, []);

  const orange = theme.palette.brand.orange.main;
  const golden = theme.palette.brand.golden.main;

  const cardBg = isDark ? 'rgba(27,54,93,0.6)' : theme.palette.background.paper;
  const cardBorder = isDark
    ? '1px solid rgba(255,255,255,0.08)'
    : '1px solid rgba(27,54,93,0.08)';

  const set = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    setErrors(errs);
    if (hasErrors(errs)) return;

    setSubmitting(true);
    const payload = {
      ownerName: form.ownerName.trim(),
      phone: form.phone,
      email: form.email,
      libraryName: form.libraryName.trim(),
      city: form.city,
      seats: Number(form.seats),
      currentSystem: form.currentSystem,
      message: form.message.trim(),
      wantsWebsite: form.wantsWebsite,
    };
    await submitLibraryLead(payload);
    track(EVT_LIBRARY_LEAD_SUBMIT, { city: payload.city });
    setSubmitting(false);
    setSnackOpen(true);
    setForm(EMPTY_FORM);
    setErrors(EMPTY_ERRORS);
  };

  return (
    <SectionContainer
      id="signup"
      eyebrow="Get started"
      title="List your library with BookMyLibrary."
      subtitle="Fill the form. We'll call you within 24 hours."
      sx={{
        background: isDark
          ? `radial-gradient(ellipse at 80% 50%, rgba(242,101,34,0.08) 0%, transparent 70%), ${theme.palette.background.default}`
          : `radial-gradient(ellipse at 80% 50%, rgba(242,101,34,0.06) 0%, transparent 70%), ${theme.palette.background.default}`,
      }}
    >
      <Grid container spacing={{ xs: 4, md: 8 }} alignItems="flex-start">
        {/* Left: pitch column */}
        <Grid item xs={12} md={5}>
          <RevealOnScroll>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 2 }}>
                Join hundreds of libraries growing with BML.
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.75, mb: 4 }}>
                Our team will set everything up for you. You just need to fill the form and we'll
                take care of the rest.
              </Typography>

              <Button
                variant="outlined"
                component="a"
                href="https://wa.me/918595675155?text=Hi%20BookMyLibrary%2C%20I%27m%20interested%20in%20listing%20my%20library"
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<WhatsApp />}
                sx={{
                  borderColor: '#25D366',
                  color: '#25D366',
                  fontWeight: 700,
                  borderWidth: 2,
                  mb: 4,
                  '&:hover': {
                    borderColor: '#1DA851',
                    color: '#1DA851',
                    background: 'rgba(37,211,102,0.06)',
                    borderWidth: 2,
                  },
                }}
              >
                Chat with us on WhatsApp
              </Button>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {TRUST_BULLETS.map((bullet) => (
                  <Box key={bullet} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CheckCircleOutlined sx={{ color: orange, fontSize: 20, flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {bullet}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </RevealOnScroll>
        </Grid>

        {/* Right: form card */}
        <Grid item xs={12} md={7}>
          <RevealOnScroll delay={0.1}>
            <Box
              ref={formRef}
              component="form"
              onSubmit={handleSubmit}
              noValidate
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: '24px',
                background: cardBg,
                border: cardBorder,
                boxShadow: isDark
                  ? '0 8px 40px rgba(0,0,0,0.3)'
                  : '0 8px 40px rgba(27,54,93,0.1)',
              }}
            >
              <Grid container spacing={2.5}>
                {/* Owner name */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Your name"
                    value={form.ownerName}
                    onChange={set('ownerName')}
                    error={Boolean(errors.ownerName)}
                    helperText={errors.ownerName}
                    fullWidth
                    required
                    inputProps={{ 'aria-label': 'Owner name' }}
                  />
                </Grid>

                {/* Phone */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Mobile number"
                    value={form.phone}
                    onChange={set('phone')}
                    error={Boolean(errors.phone)}
                    helperText={errors.phone}
                    fullWidth
                    required
                    inputProps={{
                      inputMode: 'numeric',
                      maxLength: 10,
                      'aria-label': 'Mobile number',
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">+91</InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* Email */}
                <Grid item xs={12}>
                  <TextField
                    label="Email address"
                    type="email"
                    value={form.email}
                    onChange={set('email')}
                    error={Boolean(errors.email)}
                    helperText={errors.email}
                    fullWidth
                    required
                    inputProps={{ 'aria-label': 'Email address' }}
                  />
                </Grid>

                {/* Library name */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Library name"
                    value={form.libraryName}
                    onChange={set('libraryName')}
                    error={Boolean(errors.libraryName)}
                    helperText={errors.libraryName}
                    fullWidth
                    required
                    inputProps={{ 'aria-label': 'Library name' }}
                  />
                </Grid>

                {/* City */}
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={INDIAN_CITIES}
                    value={form.city}
                    onChange={(_e, val) =>
                      setForm((prev) => ({ ...prev, city: val }))
                    }
                    freeSolo={false}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="City"
                        required
                        error={Boolean(errors.city)}
                        helperText={errors.city}
                        inputProps={{
                          ...params.inputProps,
                          'aria-label': 'City',
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Seats */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Number of seats"
                    type="number"
                    value={form.seats}
                    onChange={set('seats')}
                    error={Boolean(errors.seats)}
                    helperText={errors.seats}
                    fullWidth
                    required
                    inputProps={{ min: 1, max: 500, 'aria-label': 'Number of seats' }}
                  />
                </Grid>

                {/* Current system */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required error={Boolean(errors.currentSystem)}>
                    <InputLabel id="current-system-label">Current system</InputLabel>
                    <Select
                      labelId="current-system-label"
                      id="current-system"
                      value={form.currentSystem}
                      label="Current system"
                      onChange={set('currentSystem')}
                    >
                      {CURRENT_SYSTEM_OPTIONS.map(({ value, label }) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.currentSystem && (
                      <FormHelperText>{errors.currentSystem}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                {/* Optional message */}
                <Grid item xs={12}>
                  <TextField
                    label="Anything else you'd like to share (optional)"
                    multiline
                    rows={3}
                    value={form.message}
                    onChange={set('message')}
                    fullWidth
                    inputProps={{ maxLength: 500, 'aria-label': 'Additional message' }}
                    helperText={`${form.message.length}/500`}
                  />
                </Grid>

                {/* Website add-on checkbox */}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.wantsWebsite}
                        onChange={(e) =>
                          setForm((prev) => ({ ...prev, wantsWebsite: e.target.checked }))
                        }
                        color="primary"
                        inputProps={{ 'aria-label': 'Add library website add-on' }}
                      />
                    }
                    label={
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.4 }}
                        >
                          Add library website — ₹1,999/year
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          Optional add-on. Our team will confirm during onboarding.
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start', mt: 0.5, ml: 0 }}
                  />
                </Grid>

                {/* Submit */}
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={submitting}
                    sx={{
                      background: `linear-gradient(135deg, ${orange}, ${golden})`,
                      color: '#fff',
                      fontWeight: 700,
                      py: 1.75,
                      fontSize: '1rem',
                      borderRadius: 3,
                      boxShadow: '0 8px 24px rgba(242,101,34,0.35)',
                      '&:hover': {
                        background: `linear-gradient(135deg, ${theme.palette.brand.orange.light}, ${orange})`,
                        boxShadow: '0 12px 32px rgba(242,101,34,0.45)',
                        transform: submitting ? 'none' : 'translateY(-1px)',
                      },
                      transition: 'all 0.2s ease',
                    }}
                    startIcon={
                      submitting ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : null
                    }
                  >
                    {submitting ? 'Submitting…' : 'Submit details'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </RevealOnScroll>
        </Grid>
      </Grid>

      <Snackbar
        open={snackOpen}
        autoHideDuration={6000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackOpen(false)}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          We received your details. Our team will call you within 24 hours.
        </Alert>
      </Snackbar>
    </SectionContainer>
  );
}

export default SignupSection;
